import * as wasm from './wasm.js';
import { ensureInitialized as initWasm } from './wasm.js';
import { AddressesFacade } from './addresses/facade.js';
import { DocumentsFacade } from './documents/facade.js';
import { IdentitiesFacade } from './identities/facade.js';
import { ContractsFacade } from './contracts/facade.js';
import { TokensFacade } from './tokens/facade.js';
import { DpnsFacade } from './dpns/facade.js';
import { EpochFacade } from './epoch/facade.js';
import { ProtocolFacade } from './protocol/facade.js';
import { SystemFacade } from './system/facade.js';
import { GroupFacade } from './group/facade.js';
import { VotingFacade } from './voting/facade.js';
export class EvoSDK {
    constructor(options = {}) {
        // Apply defaults while preserving any future connection options
        const { network = 'testnet', trusted = false, addresses, ...connection } = options;
        this.options = { network, trusted, addresses, ...connection };
        this.addresses = new AddressesFacade(this);
        this.documents = new DocumentsFacade(this);
        this.identities = new IdentitiesFacade(this);
        this.contracts = new ContractsFacade(this);
        this.tokens = new TokensFacade(this);
        this.dpns = new DpnsFacade(this);
        this.epoch = new EpochFacade(this);
        this.protocol = new ProtocolFacade(this);
        this.system = new SystemFacade(this);
        this.group = new GroupFacade(this);
        this.voting = new VotingFacade(this);
    }
    get wasm() {
        if (!this.wasmSdk)
            throw new Error('SDK is not connected. Call EvoSDK#connect() first.');
        return this.wasmSdk;
    }
    get isConnected() { return !!this.wasmSdk; }
    async getWasmSdkConnected() {
        if (!this.wasmSdk) {
            await this.connect();
        }
        return this.wasmSdk;
    }
    async connect() {
        if (this.wasmSdk)
            return; // idempotent
        await initWasm();
        const { network, trusted, version, proofs, settings, logs, addresses } = this.options;
        let builder;
        // If specific addresses are provided, use them instead of network presets
        if (addresses && addresses.length > 0) {
            // Prefetch trusted quorums for the network before creating builder with addresses
            if (network === 'mainnet') {
                await wasm.WasmSdk.prefetchTrustedQuorumsMainnet();
            }
            else if (network === 'testnet') {
                await wasm.WasmSdk.prefetchTrustedQuorumsTestnet();
            }
            else if (network === 'local') {
                await wasm.WasmSdk.prefetchTrustedQuorumsLocal();
            }
            builder = wasm.WasmSdkBuilder.withAddresses(addresses, network);
        }
        else if (network === 'mainnet') {
            await wasm.WasmSdk.prefetchTrustedQuorumsMainnet();
            builder = trusted ? wasm.WasmSdkBuilder.mainnetTrusted() : wasm.WasmSdkBuilder.mainnet();
        }
        else if (network === 'testnet') {
            await wasm.WasmSdk.prefetchTrustedQuorumsTestnet();
            builder = trusted ? wasm.WasmSdkBuilder.testnetTrusted() : wasm.WasmSdkBuilder.testnet();
        }
        else if (network === 'local') {
            // Default local dashmate gateway and quorum list sidecar
            await wasm.WasmSdk.prefetchTrustedQuorumsLocal();
            builder = trusted ? wasm.WasmSdkBuilder.localTrusted() : wasm.WasmSdkBuilder.local();
        }
        else {
            throw new Error(`Unknown network: ${network}`);
        }
        if (version)
            builder = builder.withVersion(version);
        if (typeof proofs === 'boolean')
            builder = builder.withProofs(proofs);
        if (logs)
            builder = builder.withLogs(logs);
        if (settings) {
            const { connectTimeoutMs, timeoutMs, retries, banFailedAddress } = settings;
            builder = builder.withSettings(connectTimeoutMs ?? null, timeoutMs ?? null, retries ?? null, banFailedAddress ?? null);
        }
        this.wasmSdk = builder.build();
    }
    static fromWasm(wasmSdk) {
        const sdk = new EvoSDK();
        sdk.wasmSdk = wasmSdk;
        return sdk;
    }
    version() {
        return this.wasm.version();
    }
    static async setLogLevel(levelOrFilter) {
        await initWasm();
        wasm.WasmSdk.setLogLevel(levelOrFilter);
    }
    static async getLatestVersionNumber() {
        await initWasm();
        return wasm.WasmSdkBuilder.getLatestVersionNumber();
    }
    // Factory helpers that return configured instances (not connected)
    static testnet(options = {}) { return new EvoSDK({ network: 'testnet', ...options }); }
    static mainnet(options = {}) { return new EvoSDK({ network: 'mainnet', ...options }); }
    static testnetTrusted(options = {}) { return new EvoSDK({ network: 'testnet', trusted: true, ...options }); }
    static mainnetTrusted(options = {}) { return new EvoSDK({ network: 'mainnet', trusted: true, ...options }); }
    static local(options = {}) { return new EvoSDK({ network: 'local', ...options }); }
    static localTrusted(options = {}) { return new EvoSDK({ network: 'local', trusted: true, ...options }); }
    /**
     * Create an EvoSDK instance configured with specific masternode addresses.
     *
     * @param addresses - Array of HTTPS URLs to masternodes (e.g., ['https://127.0.0.1:1443'])
     * @param network - Network identifier: 'mainnet', 'testnet' (default: 'testnet')
     * @param options - Additional connection options
     * @returns A configured EvoSDK instance (not yet connected - call .connect() to establish connection)
     *
     * @example
     * ```typescript
     * const sdk = EvoSDK.withAddresses(['https://52.12.176.90:1443'], 'testnet');
     * await sdk.connect();
     * ```
     */
    static withAddresses(addresses, network = 'testnet', options = {}) {
        return new EvoSDK({ addresses, network, ...options });
    }
}
export { AddressesFacade } from './addresses/facade.js';
export { DocumentsFacade } from './documents/facade.js';
export { IdentitiesFacade } from './identities/facade.js';
export { ContractsFacade } from './contracts/facade.js';
export { TokensFacade } from './tokens/facade.js';
export { DpnsFacade } from './dpns/facade.js';
export { EpochFacade } from './epoch/facade.js';
export { ProtocolFacade } from './protocol/facade.js';
export { SystemFacade } from './system/facade.js';
export { GroupFacade } from './group/facade.js';
export { VotingFacade } from './voting/facade.js';
export { wallet } from './wallet/functions.js';
export * from './wasm.js';
